//@ts-check
/**
 * A log of real captures, kept on the device, for the capture-friction spike
 * (wip/CAPTURE_FRICTION_SPIKES.md, questions 1.1, 1.2, 1.4, 1.5, 1.12, 1.13 and 1.15).
 *
 * The questions are about what happens on a phone in a shop, and the only honest way to answer
 * them is to capture things for real and read the numbers afterwards. So each capture writes one
 * entry here — timings, what the camera handed over, how the upload went — and /lab shows them.
 *
 * Lab only, and observation only: nothing here changes what the app does, which is the rule that
 * keeps lab a rehearsal for production (see $lib/env.js). In production every call is a no-op.
 * Delete this module, /lab and its call sites once the spike is answered.
 *
 * Uses localStorage, which the UI guide otherwise forbids — an exception the owner approved for
 * the life of this spike only (WEBSITE_UI_CODING_GUIDE.md, "Never use localStorage").
 */
import { isProdHost } from "$lib/env.js";

const LOG_KEY = "tt.captureprobe.log";
const CAMERA_KEY = "tt.captureprobe.cameraOpenedAt";
const MAX_ENTRIES = 40;

/** @param {() => void} fn */
function inLab(fn) {
  if (isProdHost()) return;
  try {
    fn();
  } catch {
    // Storage can be full or blocked; a missing log entry is not worth a broken capture.
  }
}

/** Note that the camera is about to take over the screen (question 1.12). */
export function markCameraOpening() {
  inLab(() => localStorage.setItem(CAMERA_KEY, String(Date.now())));
}

/** The camera handed back a photo, or was cancelled. Returns how long it was open, in ms. */
export function markCameraReturned() {
  let elapsed = 0;
  inLab(() => {
    const opened = Number(localStorage.getItem(CAMERA_KEY));
    if (opened) elapsed = Date.now() - opened;
    localStorage.removeItem(CAMERA_KEY);
  });
  return elapsed;
}

/**
 * Called once when the app starts. If the camera marker is still set, the app was reloaded while
 * the camera had the screen — iOS killed it — and whatever was in the capture sheet is gone.
 */
export function checkForCameraReload() {
  inLab(() => {
    const opened = Number(localStorage.getItem(CAMERA_KEY));
    localStorage.removeItem(CAMERA_KEY);
    if (!opened || Date.now() - opened > 10 * 60 * 1000) return;
    record({ kind: "reloaded-during-camera", cameraOpenForMs: Date.now() - opened });
  });
}

/**
 * What a paste actually carried, whether or not it attached a photo. The open question is whether
 * iOS passes a copied image to a text field's paste at all, and if not, what it passes instead.
 *
 * @param {ClipboardEvent} event
 * @param {boolean} attached
 */
export function recordPaste(event, attached) {
  inLab(() => {
    const data = event.clipboardData;
    record({
      kind: "paste",
      attached,
      types: Array.from(data?.types || []),
      files: Array.from(data?.files || []).map((f) => ({ type: f.type || "unknown", bytes: f.size })),
    });
  });
}

/** @param {Record<string, any>} entry */
export function record(entry) {
  inLab(() => {
    const log = readLog();
    log.unshift({ at: new Date().toISOString(), ...entry });
    localStorage.setItem(LOG_KEY, JSON.stringify(log.slice(0, MAX_ENTRIES)));
  });
}

/** @returns {any[]} newest first */
export function readLog() {
  try {
    return JSON.parse(localStorage.getItem(LOG_KEY) || "[]");
  } catch {
    return [];
  }
}

export function clearLog() {
  inLab(() => localStorage.removeItem(LOG_KEY));
}
